import React, { useEffect, useMemo, useState } from 'react'

import { GetCustomerCartQuery } from '../../graphql/magento'
import useIsMounted from '../../hooks/useIsMounted'
import { readCartDispensaryPromoCodes } from '../../lib/cart/dispensary-promo'
import { usePromoCodeUtilities } from '../../lib/cart/hooks/usePromoCodeUtilities'
import { logAndCaptureException } from '../../utils/errorTools'
import { discountAmount } from '../../utils/priceTools'
import CartSummaryRow from './CartSummaryRow'

export interface CartAppliedCouponsProps {
  cart: NonNullable<GetCustomerCartQuery['customerCart']>
  className?: string
}

const CartAppliedCoupons: React.FC<CartAppliedCouponsProps> = ({ cart, className }) => {
  const {
    applied_coupons: appliedCoupons,
    dispensary_promo_code: dispensaryPromoCode,
    prices,
  } = cart

  const cartDispensaryPromoCodes = useMemo(
    () => (dispensaryPromoCode ? readCartDispensaryPromoCodes(dispensaryPromoCode) : undefined),
    [dispensaryPromoCode],
  )
  const { concealCode, loading: utilitiesLoading } = usePromoCodeUtilities({
    cartDispensaryPromoCodes,
  })

  const [commaSeparatedCoupons, setCommaSeparatedCoupons] = useState<string>('')
  const isMounted = useIsMounted()

  useEffect(() => {
    if (!appliedCoupons || utilitiesLoading) {
      setCommaSeparatedCoupons('')
      return
    }
    appliedCoupons
      .reduce(async (acc: Promise<string>, coupon) => {
        if (!coupon) return acc
        const awaitedAcc = await acc
        return `${awaitedAcc}${awaitedAcc.length > 0 ? ', ' : ''}${await concealCode(coupon.code)}`
      }, Promise.resolve(''))
      .then((value) => {
        if (isMounted.current) {
          setCommaSeparatedCoupons(value)
        }
      })
      .catch(logAndCaptureException)
  }, [appliedCoupons, concealCode, isMounted, utilitiesLoading])

  if (!appliedCoupons) {
    return null
  }

  const loadingProps = utilitiesLoading ? { 'data-promo-loading': true } : undefined

  return (
    <CartSummaryRow className={className}>
      <div {...loadingProps}>Promo: {commaSeparatedCoupons}</div>
      <div>-{discountAmount({ appliedCoupons, prices })}</div>
    </CartSummaryRow>
  )
}

export default CartAppliedCoupons
